import { LogOut, Menu } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { AssistantSwitcher } from "@/features/character/AssistantSwitcher";
import { useAuth } from "@/hooks/useAuth";

import { BrandMark } from "./BrandMark";
import { MobileSidebarContents } from "./Sidebar";
import { ThemeToggle } from "./ThemeProvider";

/**
 * Top bar:
 *   mobile (<md): hamburger drawer-toggle + MyAiFriend brand + theme toggle +
 *                 icon-only logout. Email is dropped (space is precious).
 *   desktop (md+): same theme/email/logout; no hamburger.
 *
 * Page titles live inside each page (for breadcrumb context), not here.
 */
export function Header() {
  const { email, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-2 border-b border-border/70 bg-background/95 px-3 sm:px-6 lg:px-8">
      {/* Hamburger — mobile only. Desktop has the static sidebar instead. */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden" aria-label="Меню">
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0">
          <SheetTitle className="sr-only">Меню</SheetTitle>
          <MobileSidebarContents onNavigate={() => setMobileMenuOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Mobile brand — desktop gets it inside the sidebar itself. */}
      <div className="flex items-center gap-2.5 md:hidden">
        <BrandMark className="h-8 w-8" />
        <span className="text-sm font-extrabold tracking-[-0.03em]">MyAiFriend</span>
      </div>

      <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
        <AssistantSwitcher />
        <ThemeToggle />
        {email && (
          <span className="mx-1 hidden max-w-64 truncate rounded-full border bg-card/70 px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-sm xl:inline">
            {email}
          </span>
        )}
        <Button
          variant="outline"
          size="sm"
          className="hidden bg-card/70 sm:inline-flex"
          onClick={() => {
            logout();
            window.location.href = "/cabinet/login";
          }}
        >
          <LogOut className="mr-2 h-4 w-4" /> Выйти
        </Button>
        {/* Narrow screens: icon-only logout to save horizontal space. */}
        <Button
          variant="ghost"
          size="icon"
          className="sm:hidden"
          aria-label="Выйти"
          onClick={() => {
            logout();
            window.location.href = "/cabinet/login";
          }}
        >
          <LogOut className="h-5 w-5" />
        </Button>
      </div>
    </header>
  );
}
